import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from 'react';
// --- external imports ------

import firebase from '~/authentication/lib/firebase';
import classifyAuthError, {
	AUTH_ERROR_CLASS,
	isRetryableAuthError
} from '~/authentication/lib/classifyAuthError';
import runWithRetry from '~/authentication/lib/runWithRetry';
import resetAnalyticsIdentity from '~/analytics/identity';
import { clearUnusableSignIn } from '~/authentication/lib/unusableSignIn';
import { clearSentLinkNotice } from '~/authentication/lib/sentLinkNotice';
// --- internal imports ------

import { getTradersUserData } from './apis';
import { parseApiResponse } from '~/authentication/lib/errors';
// --- local imports ------

const UserContext = createContext({user: undefined});

// A user system that is briefly unreachable is retried in place rather than
// turned into a logout. The screens keep their loading cursor while this runs.
const AUTH_RETRY_OPTIONS = {
	retries: 2,
	baseDelayMs: 800,
	shouldRetry: isRetryableAuthError
};

function UserProvider(props) {
	const [firebaseUser, setFirebaseUser] = useState(undefined);
	const [tradersUserData, setTradersUserData] = useState(undefined);
	const [pendingAuthRequests, setPendingAuthRequests] = useState(0);
	const [hasSettledInitialAuth, setHasSettledInitialAuth] = useState(false);
	// Whether Firebase has reported on the session at all yet — a different
	// question from `hasSettledInitialAuth`, which is about the *user system*.
	// Both run on a cold load and neither waits for the other, so a screen that
	// reads `firebaseUser` needs to know which of "no session" and "no answer
	// yet" it is looking at. See the comment on `hasSettledFirebaseAuth` below.
	const [hasSettledFirebaseAuth, setHasSettledFirebaseAuth] = useState(false);
	const [tradersToken, setTradersToken] = useState(localStorage.getItem('tradersToken') ?? '');
	const [authError, setAuthError] = useState(null);

	const latestTokenRef = useRef(null);
	// The last Firebase user this provider has taken up, by object identity.
	// Whichever of the two routes to a fresh sign-in arrives first claims it and
	// the other finds it already claimed — see `adoptSignedInUser`.
	const adoptedUserRef = useRef(null);
	// Which auth chain is the current one. Retries stretch a chain over seconds,
	// so two of them overlap routinely — a cold load restoring a saved token while
	// Firebase logs the player in, or a retry starting before the attempt it
	// replaces has given up. Only the newest may write to the context: an older
	// chain closes over the token it started with, and letting its catch block run
	// would clear a session that a newer one had already established.
	const currentAuthRunRef = useRef(0);

	const beginAuthRun = useCallback(() => {
		currentAuthRunRef.current += 1;
		setPendingAuthRequests((count) => count + 1);
		return currentAuthRunRef.current;
	}, []);

	const endAuthRun = useCallback(() => {
		setPendingAuthRequests((count) => Math.max(0, count - 1));
		setHasSettledInitialAuth(true);
	}, []);

	const isCurrentAuthRun = useCallback((runId) => currentAuthRunRef.current === runId, []);

	// Everything that ends a session on this machine, in one place.
	//
	// Three paths in this file end one: a deliberate logout, a stored token the
	// user system refuses, and a login Firebase accepted that our own backend
	// then turned down. Each used to drop the token and clear the state by hand,
	// which is how the analytics identity came to be reset on the first of them
	// and left attached on the other two — and the identity is the part that
	// survives a reload, so those two are the shared-machine case, not an edge of
	// it. A player identified in an earlier page load who reloads into a refused
	// token was logged out with their email still on every event the next person
	// fired. Callers add what is theirs alone; the parts every exit owes get done
	// here, so a fourth exit cannot forget one.
	//
	// Dropped from localStorage directly rather than through `clearSessionData`:
	// that module reaches the inbox, and through it Dexie, which has no business
	// in the auth bundle.
	const discardSession = useCallback(() => {
		// The token goes first and synchronously: it is what the guarded routes and
		// `handleOnAuthStateChanged` read, so the session is over from the app's
		// point of view before anything else is attempted.
		localStorage.removeItem('tradersToken');
		setTradersToken('');
		setTradersUserData(null);

		// The session is held in a place that list does not name: Heap and
		// LogRocket, told who this player is on the way in and saying so until they
		// are told otherwise. See `~/analytics/identity`.
		resetAnalyticsIdentity();
	}, []);

	const fetchTradersUserData = useCallback(async () => {
		const runId = beginAuthRun();

		if (!tradersToken) {
			if (isCurrentAuthRun(runId)) {
				setTradersUserData(null);
			}
			endAuthRun();
			return;
		}

		try {
			const newTradersUserData = await runWithRetry(
				() => getTradersUserData(tradersToken),
				AUTH_RETRY_OPTIONS
			);

			if (!isCurrentAuthRun(runId)) {
				return;
			}

			setTradersUserData(newTradersUserData);
			setAuthError(null);

			if (window.heap && newTradersUserData?.email) {
				window.heap.identify?.(newTradersUserData.email);
			}
		}
		catch (error) {
			if (!isCurrentAuthRun(runId)) {
				return;
			}

			console.error('Error fetching traders user data.');
			setAuthError(error);

			// A user system that could not be reached says nothing about the
			// player's session. Dropping the token here would log a valid player
			// out over a deploy or a dropped connection, so the session is kept
			// and the screen offers a retry instead.
			if (classifyAuthError(error) !== AUTH_ERROR_CLASS.TRANSIENT) {
				discardSession();
			}
		}
		finally {
			endAuthRun();
		}
	}, [tradersToken, beginAuthRun, endAuthRun, isCurrentAuthRun, discardSession]);

	const loginPlayer = useCallback(async (newUser) => {
		const runId = beginAuthRun();

		setTradersUserData(undefined);
		setFirebaseUser(newUser);

		try {
			const jsonResponse = await runWithRetry(async () => {
				const firebaseToken = await newUser.getIdToken();
				const response = await fetch(`${process.env.REACT_APP_USERBASE_SERVER_BASE_URL}/auth/login-player`, {
					method: 'POST',
					headers: {
						'Authorization': `Bearer ${firebaseToken}`,
						'Content-Type': 'application/json'
					},
					signal: AbortSignal.timeout(15000)
				});

				return parseApiResponse(response, 'Login failed');
			}, AUTH_RETRY_OPTIONS);

			if (!isCurrentAuthRun(runId)) {
				return;
			}

			const { token, user } = jsonResponse;
			latestTokenRef.current = token;
			setTradersUserData(user);
			localStorage.setItem('tradersToken', token);
			setTradersToken(token);
			setAuthError(null);

			if (window.heap && user?.email) {
				window.heap.identify?.(user.email);
			}
		}
		catch (error) {
			if (!isCurrentAuthRun(runId)) {
				return;
			}

			console.error('Error authenticating with backend.');
			setAuthError(error);

			// Firebase accepted the player; it is our own backend that could not be
			// reached. Signing them out here would turn a blip into a lost session,
			// so the Firebase session is left alone and the screen offers a retry.
			if (isRetryableAuthError(error)) {
				return;
			}

			discardSession();
			firebase.signOut(); // Break the Firebase authenticated loop automatically
		}
		finally {
			endAuthRun();
		}
	}, [beginAuthRun, endAuthRun, isCurrentAuthRun, discardSession]);

	// Offered to the screen that exchanges an email link, for the sign-in Firebase
	// will not announce.
	//
	// `onAuthStateChanged` is the only route a new session normally takes to this
	// provider, and Firebase fires it only when the *uid* changes:
	//
	//     this.idTokenSubscription.next(this.currentUser);        // always
	//     const currentUid = this.currentUser?.uid ?? null;
	//     if (this.lastNotifiedUid !== currentUid) { ... }        // auth state
	//
	// A player who asks the recovery screen for a new link and spends it is
	// re-signing in as the account already signed in, so the uid does not move
	// and the notification never comes. The exchange succeeds, `loginPlayer`
	// never runs, and the context keeps the *previous* user object — the one
	// whose `auth_time` is too old to set a password. The screen then holds a
	// loading cursor for a login that will not happen, and a player who gets
	// back to the form is refused all over again. That is the dead end the link
	// was sent to break, one step further along.
	//
	// So the screen hands the fresh user over directly. Idempotent by object
	// identity rather than by uid: the exchange and `onAuthStateChanged` are
	// handed the same `User` instance, and either may arrive first — a uid
	// comparison cannot tell "already taken up" from "same account, fresh
	// sign-in", which is exactly the case this exists for. `retryAuthCheck`
	// deliberately re-runs `loginPlayer` for a user already taken up, so the
	// guard lives here rather than inside it.
	const adoptSignedInUser = useCallback((newUser) => {
		if (!newUser || adoptedUserRef.current === newUser) {
			return undefined;
		}

		adoptedUserRef.current = newUser;

		return loginPlayer(newUser);
	}, [loginPlayer]);

	// The one way out of a session, for every screen that offers one.
	//
	// A session is held in three places — the stored token, this provider's
	// state, and Firebase — and each of the four screens that logged a player
	// out used to drop only the last of them. That is the one order in which a
	// logout does nothing: `handleOnAuthStateChanged` reads the stored token
	// synchronously and treats its presence as a sign-out nobody asked for, so
	// with the token still on disk the handler returns early and the player
	// keeps a session the screen believes is over. The token then outlives the
	// logout and is spent by whoever opens the app next on that machine — which
	// is how a registration link for one player was answered with another
	// player's account.
	//
	// So the token goes first, and Firebase last.
	const logOutPlayer = useCallback(async () => {
		discardSession();
		setFirebaseUser(null);
		setAuthError(null);

		// Both describe the session being ended here, so they go with it rather than
		// waiting on the machine for whoever signs in next.
		clearUnusableSignIn();
		clearSentLinkNotice();

		try {
			await firebase.signOut();
		}
		catch (error) {
			// The stored token is already gone, which is what the routes read, so
			// the player is logged out of this app either way. A Firebase sign-out
			// that failed must not leave the caller unable to finish.
			console.error('Failed to sign out of Firebase.', error);
		}
	}, [discardSession]);

	const handleOnAuthStateChanged = useCallback(async (newUser) => {
		// Recorded first, and for both answers. Firebase calls this once on
		// startup whichever way the session went, and that call is the only
		// evidence a screen gets that the question has been asked at all —
		// `firebaseUser` itself cannot carry it, because the no-session branch
		// below returns early while a stored token is present and so never sets
		// the value. Left to speak for itself, `firebaseUser` says `undefined`
		// both while Firebase is still looking and after it has finished and
		// found nothing.
		setHasSettledFirebaseAuth(true);

		if (newUser) {
			// Through the same claim the exchange uses, so that whichever of the
			// two gets here first does the login and the other stands down.
			await adoptSignedInUser(newUser);
		}
		else {
			// Read synchronously from localStorage instead of React state to avoid stale closure during catch block's batched signOut
			if (localStorage.getItem('tradersToken')) {
				return;
			}
			setFirebaseUser(null);
			setTradersUserData(null);
			setHasSettledInitialAuth(true);
		}
	}, [adoptSignedInUser]);

	// Offered to the screens for a failure that is worth another attempt — the
	// user system was unreachable, so whichever request failed is simply run
	// again rather than the player being asked to sign in from scratch.
	const retryAuthCheck = useCallback(() => {
		setAuthError(null);

		if (firebaseUser) {
			return loginPlayer(firebaseUser);
		}

		return fetchTradersUserData();
	}, [firebaseUser, loginPlayer, fetchTradersUserData]);

	useEffect(() => {
		if (latestTokenRef.current === tradersToken) {
			latestTokenRef.current = null;
			return;
		}
		fetchTradersUserData();
	}, [tradersToken, fetchTradersUserData]);

	useEffect(() => {
		const unsub = firebase.onAuthStateChanged(handleOnAuthStateChanged);
		return unsub;
	}, [handleOnAuthStateChanged]);

	// Derived from a count rather than held as a flag: two auth chains overlap
	// routinely, and a single boolean let whichever finished first report that the
	// check was over while the other was still retrying — for up to the length of
	// a full backoff. `AuthRoute` latches on that answer, so it has to be true for
	// as long as *any* request is in flight, not just the last one to report.
	const isCheckingInitialAuthData = pendingAuthRequests > 0 || !hasSettledInitialAuth;

	// True only while the provider has a request of its own in flight — a session
	// to restore or a player to log in. A visitor with neither is not "busy", so
	// screens do not flash a loading cursor on every cold page load.
	const isAuthBusy = isCheckingInitialAuthData && (!!tradersToken || !!firebaseUser);

	return (
		<UserContext.Provider
			value={{
				firebaseUser,
				tradersUserData,
				hasSettledFirebaseAuth,
				isCheckingInitialAuthData,
				isAuthBusy,
				setTradersUserData,
				authError,
				setAuthError,
				retryAuthCheck,
				adoptSignedInUser,
				logOutPlayer
			}}
		>
			{props.children}
		</UserContext.Provider>
	);
}

export function useUserContext() {
	return useContext(UserContext);
}

export default UserProvider;

export { UserContext };
